<template>
  <div v-if="open" class="cima-overlay" @click.self="$emit('close')">
    <div class="cima-modal">
      <div class="cima-header">
        <div class="cima-header-top">
          <h3 class="cima-title">{{ trans(tKey("title")) }}</h3>
          <button
            class="btn btn-secondary"
            type="button"
            :disabled="running"
            @click="$emit('close')"
          >
            {{ trans(cKey("close")) }}
          </button>
        </div>

        <div class="cima-toolbar">
          <template v-if="availableActions.length > 1">
            <label class="cima-label">{{ trans(cKey("action")) }}</label>
            <select
              v-model="selectedAction"
              class="form-control cima-select"
              :disabled="running"
            >
              <option
                v-for="action in availableActions"
                :key="action"
                :value="action"
              >
                {{ trans(tKey(action)) }}
              </option>
            </select>
            <button
              class="btn btn-primary"
              type="button"
              :disabled="running || !executableUsers.length"
              @click="execute"
            >
              {{ trans(cKey("execute")) }}
            </button>
          </template>

          <button
            v-else
            class="btn btn-primary"
            type="button"
            :disabled="running || !executableUsers.length"
            @click="execute"
          >
            {{ trans(tKey("verify")) }}
          </button>

          <div v-if="running" class="cima-help">
            {{ progress }} / {{ executableUsers.length }}
          </div>
        </div>
      </div>

      <div class="cima-body">
        <div v-if="loading">{{ trans(cKey("loading")) }}</div>
        <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
        <div v-else-if="!users.length">
          {{ trans(cKey("no_users")) }}
        </div>

        <template v-else>
          <div v-if="connectionError" class="alert alert-danger">
            {{ connectionError }}
          </div>

          <table class="table">
            <thead>
              <tr>
                <th>{{ trans(cKey("col_name")) }}</th>
                <th v-if="syncMethod === 'hsa_id'">
                  {{ trans(cKey("col_hsa_id")) }}
                </th>
                <th>{{ trans(tKey("col_username")) }}</th>
                <th>{{ trans(cKey("col_status")) }}</th>
                <th>{{ trans(cKey("col_last_check")) }}</th>
                <th>{{ trans(cKey("col_last_error")) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in users" :key="u.id" :class="rowClass(u)">
                <td>{{ u.full_name }}</td>
                <td v-if="syncMethod === 'hsa_id'">{{ u.hsa_id || "" }}</td>
                <td>
                  <template v-if="u.username">
                    {{ u.username }}
                  </template>
                  <span v-else-if="isDisabled(u)" class="cima-no-username">
                    {{ trans(cKey("no_username")) }}
                  </span>
                </td>
                <td class="cima-status-cell">
                  <span v-if="rowState[u.id]?.state === 'running'">{{
                    trans(cKey("running"))
                  }}</span>
                  <span v-else>{{ statusSymbol(u) }}</span>
                </td>
                <td>
                  {{
                    u.external_identity?.last_checked_at ||
                    rowState[u.id]?.last_checked_at ||
                    "\u2013"
                  }}
                </td>
                <td class="cima-error-cell">
                  {{
                    u.external_identity?.last_error ||
                    rowState[u.id]?.error ||
                    "\u2013"
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { practitionerHttpService } from "../../app/practitioner-portal/store/practitioner-http-service";

export default {
  props: {
    open: { type: Boolean, default: false },
    centerId: { type: [String, Number], required: true },
    integration: { type: String, required: true },
    translationPrefix: { type: String, required: true },
  },
  data() {
    return {
      users: [],
      loading: false,
      error: null,
      selectedAction: "verify",
      running: false,
      progress: 0,
      rowState: {},
      syncMethod: "manual",
      availableActions: [],
      connectionError: null,
    };
  },
  computed: {
    executableUsers() {
      return this.users.filter((u) => !this.isDisabled(u));
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(val) {
        if (val) this.load();
      },
    },
    centerId() {
      if (this.open) this.load();
    },
  },
  methods: {
    tKey(suffix) {
      return this.translationPrefix + "." + suffix;
    },

    cKey(suffix) {
      return "feature.cima." + suffix;
    },

    apiBase() {
      return (
        "/api/internal/centers/" +
        this.centerId +
        "/external-identities/users/" +
        this.integration
      );
    },

    async load() {
      this.loading = true;
      this.error = null;
      this.connectionError = null;

      try {
        const res = await practitionerHttpService.get(this.apiBase());

        this.syncMethod = res.data?.sync_method || "manual";
        this.availableActions = res.data?.available_actions || ["verify"];
        this.selectedAction = this.availableActions[0];
        this.users = res.data?.users || [];

        this.rowState = Object.fromEntries(
          this.users.map((u) => {
            const status = u.external_identity?.status || "unverified";
            return [
              u.id,
              {
                state: this.stateFromStatus(status),
                error: u.external_identity?.last_error || null,
                status,
                last_checked_at: u.external_identity?.last_checked_at || null,
              },
            ];
          }),
        );
      } catch (e) {
        this.error =
          e?.response?.data?.message || this.trans(this.cKey("load_failed"));
      } finally {
        this.loading = false;
      }
    },

    stateFromStatus(status) {
      if (status === "verified") return "success";
      if (status === "failed") return "failed";
      return "idle";
    },

    statusSymbol(u) {
      const st = this.rowState[u.id]?.status || u.external_identity?.status;
      if (st === "verified") return "\u2713";
      if (st === "failed") return "\u2715";
      return "\u2013";
    },

    isDisabled(u) {
      if (this.selectedAction === "verify" && !u.username) {
        return true;
      }
      if (
        this.selectedAction === "sync_and_verify" &&
        !u.hsa_id &&
        !u.username
      ) {
        return true;
      }
      return false;
    },

    rowClass(u) {
      if (this.isDisabled(u)) {
        return "cima-row-disabled";
      }

      if (this.rowState[u.id]?.state === "running") return "cima-row-running";

      const st = this.rowState[u.id]?.status || u.external_identity?.status;
      if (st === "verified") return "cima-row-verified";
      if (st === "failed") return "cima-row-failed";
      return "cima-row-unverified";
    },

    async execute() {
      this.running = true;
      this.progress = 0;
      this.connectionError = null;

      for (const u of this.executableUsers) {
        this.rowState[u.id] = {
          ...this.rowState[u.id],
          state: "running",
          error: null,
        };

        try {
          const res = await practitionerHttpService.post(
            this.apiBase() + "/" + u.id + "/execute",
            { action: this.selectedAction },
          );

          const errorType = res.data?.error_type || null;

          if (errorType === "connection") {
            this.connectionError =
              res.data?.error || this.trans(this.tKey("connection_error"));
            this.rowState[u.id] = {
              ...this.rowState[u.id],
              state: "failed",
              status: "failed",
              error: res.data?.error || null,
            };
            this.progress++;
            break;
          }

          const status =
            res.data?.external_identity?.status || res.data?.status;
          const error =
            res.data?.external_identity?.last_error || res.data?.error || null;
          const username =
            res.data?.username ||
            res.data?.external_identity?.external_id ||
            null;
          const lastChecked =
            res.data?.external_identity?.last_checked_at ||
            res.data?.last_checked_at ||
            null;

          this.rowState[u.id] = {
            ...this.rowState[u.id],
            state: this.stateFromStatus(status),
            status: status || "unverified",
            error,
            last_checked_at: lastChecked,
          };

          if (username) {
            u.username = username;
          } else if (this.selectedAction !== "verify" && u.hsa_id) {
            u.username = u.hsa_id;
          }

          if (!u.external_identity) {
            u.external_identity = {};
          }

          u.external_identity.status = status;
          u.external_identity.last_checked_at = lastChecked;
          u.external_identity.last_error = error;
        } catch (e) {
          const error =
            e?.response?.data?.error || this.trans(this.cKey("action_failed"));
          this.connectionError = error;
          this.rowState[u.id] = {
            ...this.rowState[u.id],
            state: "failed",
            status: "failed",
            error,
          };

          if (!u.external_identity) {
            u.external_identity = {};
          }

          u.external_identity.status = "failed";
          u.external_identity.last_error = error;
          this.progress++;
          break;
        }

        this.progress++;
      }

      this.running = false;
    },
  },
};
</script>
